import React, { useEffect, useState } from "react";
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { db, auth } from "../../lib/firebase";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { format } from "date-fns";
import { Bell, BellRing, Trash2, CheckCircle, X } from "lucide-react";

interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: { seconds: number };
  data?: any;
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case "payment_uploaded":
    case "payment_approved":
    case "payment_rejected":
      return <BellRing className="h-5 w-5 text-blue-500" />;
    case "schedule_requested":
    case "schedule_approved":
    case "schedule_rejected":
    case "reschedule_requested":
      return <BellRing className="h-5 w-5 text-green-500" />;
    case "schedule_completed":
      return <CheckCircle className="h-5 w-5 text-purple-500" />;
    case "license_code_request":
      return <BellRing className="h-5 w-5 text-orange-500" />;
    default:
      return <Bell className="h-5 w-5 text-gray-500" />;
  }
};

const getNotificationColor = (type: string) => {
  switch (type) {
    case "payment_uploaded":
    case "schedule_requested":
    case "reschedule_requested":
    case "license_code_request":
      return "bg-yellow-50 border-yellow-200";
    case "payment_approved":
    case "schedule_approved":
      return "bg-green-50 border-green-200";
    case "payment_rejected":
    case "schedule_rejected":
      return "bg-red-50 border-red-200";
    case "schedule_completed":
      return "bg-purple-50 border-purple-200";
    default:
      return "bg-blue-50 border-blue-200";
  }
};

const StaffNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [currentStaffId, setCurrentStaffId] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setCurrentStaffId(auth.currentUser.uid);

    // Set up real-time listener for all notifications, then filter client-side
    const notificationsQuery = query(
      collection(db, "notifications"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const allNotifications = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Notification[];
        
        // Filter to show only notifications for current staff member
        const staffNotifications = allNotifications.filter(
          notification => notification.userId === auth.currentUser?.uid
        );
        
        console.log("All notifications:", allNotifications.length);
        console.log("Staff notifications for user", auth.currentUser?.uid, ":", staffNotifications.length);
        console.log("Staff notifications:", staffNotifications);
        
        setNotifications(staffNotifications);
        setLoading(false);
        setError(null); // Clear any previous errors
      },
      (error) => {
        console.error("Error fetching notifications:", error);
        setError("Failed to load notifications. Please try again.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const markAsRead = async (notificationId: string) => {
    try {
      setError(null); // Clear any previous errors
      await updateDoc(doc(db, "notifications", notificationId), {
        read: true,
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
      setError("Failed to mark notification as read. Please try again.");
    }
  };

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length === 0) return;
    
    setMarkingAllRead(true);
    try {
      setError(null); // Clear any previous errors
      
      // Update notifications in batches
      const batchSize = 10;
      for (let i = 0; i < unreadNotifications.length; i += batchSize) {
        const batch = unreadNotifications.slice(i, i + batchSize);
        await Promise.all(
          batch.map(notification =>
            updateDoc(doc(db, "notifications", notification.id), { read: true })
          )
        );
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      setError("Failed to mark all notifications as read. Please try again.");
    } finally {
      setMarkingAllRead(false);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      setError(null); // Clear any previous errors
      await deleteDoc(doc(db, "notifications", notificationId));
    } catch (error) {
      console.error("Error deleting notification:", error);
      setError("Failed to delete notification. Please try again.");
    }
  };

  const clearAllNotifications = async () => {
    if (notifications.length === 0) return;
    
    setClearingAll(true);
    try {
      setError(null); // Clear any previous errors
      
      // Delete notifications in batches to avoid overwhelming Firestore
      const batchSize = 10;
      for (let i = 0; i < notifications.length; i += batchSize) {
        const batch = notifications.slice(i, i + batchSize);
        await Promise.all(
          batch.map(notification =>
            deleteDoc(doc(db, "notifications", notification.id))
          )
        );
      }
    } catch (error) {
      console.error("Error clearing all notifications:", error);
      setError("Failed to clear all notifications. Please try again.");
    } finally {
      setClearingAll(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Notifications</h1>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-blue-600 border-t-transparent"></div>
            <p className="mt-2">Loading notifications...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold">Notifications</h1>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="bg-red-100 text-red-700">
              {unreadCount} unread
            </Badge>
          )}
        </div>
        
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={markAllAsRead}
              disabled={markingAllRead}
              className="flex items-center gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              {markingAllRead ? "Marking..." : "Mark All Read"}
            </Button>
          )}
          {notifications.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllNotifications}
              disabled={clearingAll}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
              {clearingAll ? "Clearing..." : "Clear All"}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex justify-between items-center">
          <span>{error}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div className="space-y-4">
        {notifications.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications</h3>
              <p className="text-gray-500">You're all caught up! No new notifications to show.</p>
            </CardContent>
          </Card>
        ) : (
          notifications.map((notification) => (
            <Card
              key={notification.id}
              className={`${getNotificationColor(notification.type)} ${
                !notification.read ? "border-l-4 border-l-blue-500" : ""
              }`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {getNotificationIcon(notification.type)}
                    <div className="flex-1">
                      <CardTitle className="text-base font-medium flex items-center gap-2">
                        {notification.title}
                        {!notification.read && (
                          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                            New
                          </Badge>
                        )}
                      </CardTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        {format(new Date(notification.createdAt.seconds * 1000), "MMM dd, yyyy 'at' h:mm a")}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    {!notification.read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => markAsRead(notification.id)}
                        className="h-8 w-8 p-0"
                        title="Mark as read"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteNotification(notification.id)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                      title="Delete notification"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default StaffNotifications;
